import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import swal from "sweetalert";
import { db, functions } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useRecoveryDeleteAccess } from "../../../hook/useRecoveryDeleteAccess";
import PageTitle from "../../layouts/PageTitle";
import {
  filterRecoveryRecordsByHn,
  getBookingRecoveryHn,
  RECOVERY_HN_FILTER_ALL,
  RECOVERY_HN_FILTER_EMPTY,
  RECOVERY_HN_FILTER_WITH_HN,
} from "../RecoveryHnUtils";
import Toast, { notifyError, notifyTopRight } from "../toast/SetToast";

const BULK_EMPTY_HN_WORKING_ID = "bulk-empty-hn";

const formatDateTime = (value) => {
  if (!value) return "-";

  let date = value;
  if (value && typeof value.toDate === "function") {
    date = value.toDate();
  } else if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRecordText = (record) => {
  const summary = record.summary || {};
  const bookingProps = (record.booking && record.booking.extendedProps) || {};

  return [
    summary.order,
    bookingProps.order,
    getBookingRecoveryHn(record),
    summary.patientName,
    bookingProps.firstname,
    bookingProps.lastname,
    summary.node,
    bookingProps.node,
    record.deletedBy && record.deletedBy.displayName,
    record.deletedBy && record.deletedBy.email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getSummaryValue = (record, field) => {
  const summary = record.summary || {};
  const bookingProps = (record.booking && record.booking.extendedProps) || {};

  if (field === "hn") return getBookingRecoveryHn(record) || "-";

  return summary[field] || bookingProps[field] || "-";
};

const BookingRecovery = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [hnFilter, setHnFilter] = useState(RECOVERY_HN_FILTER_WITH_HN);
  const [emptyHnCount, setEmptyHnCount] = useState(null);
  const [emptyHnCountLoading, setEmptyHnCountLoading] = useState(true);
  const { hasRecoveryDeleteAccess } = useRecoveryDeleteAccess();

  const restoreDeletedBooking = httpsCallable(functions, "restoreDeletedBooking");
  const permanentlyDeleteDeletedBooking = httpsCallable(
    functions,
    "permanentlyDeleteDeletedBooking",
  );
  const permanentlyDeleteEmptyHnBookingRecoveryRecords = httpsCallable(
    functions,
    "permanentlyDeleteEmptyHnBookingRecoveryRecords",
  );
  const currentRoles =
    currentUser && Array.isArray(currentUser.roles) ? currentUser.roles : [];
  const isSuperAdmin = currentRoles.includes("super_admin");

  const loadRecoveryRecords = useCallback(async () => {
    setLoading(true);
    if (isSuperAdmin) setEmptyHnCountLoading(true);
    try {
      const recoveryQuery = query(
        collection(db, "deletedBookings"),
        orderBy("deletedAt", "desc"),
        limit(250),
      );
      const snapshot = await getDocs(recoveryQuery);
      setRecords(
        snapshot.docs.map((record) => ({ id: record.id, ...record.data() })),
      );
    } catch (error) {
      console.error("Unable to load booking recovery records", error);
      notifyError(
        `Unable to load Booking Recovery: ${(error && error.message) || error}`,
      );
    } finally {
      setLoading(false);
    }

    if (!isSuperAdmin) {
      setEmptyHnCount(null);
      setEmptyHnCountLoading(false);
      return;
    }

    try {
      const getEmptyHnBookingRecoveryRecordCount = httpsCallable(
        functions,
        "getEmptyHnBookingRecoveryRecordCount",
      );
      const response = await getEmptyHnBookingRecoveryRecordCount();
      setEmptyHnCount(
        Number(response && response.data && response.data.emptyHnCount) || 0,
      );
    } catch (error) {
      console.error("Unable to load empty-HN booking recovery count", error);
      setEmptyHnCount(null);
      notifyError(
        `Unable to load empty-HN total: ${(error && error.message) || error}`,
      );
    } finally {
      setEmptyHnCountLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadRecoveryRecords();
  }, [loadRecoveryRecords]);

  const visibleRecords = useMemo(() => {
    const hnFilteredRecords = filterRecoveryRecordsByHn(
      records,
      hnFilter,
      getBookingRecoveryHn,
    );
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) return hnFilteredRecords;

    return hnFilteredRecords.filter((record) =>
      getRecordText(record).includes(normalizedSearch),
    );
  }, [hnFilter, records, searchText]);

  const restoreBooking = async (record) => {
    if (record.status !== "deleted" || workingId) return;

    const order = getSummaryValue(record, "order");
    const confirmed = await swal({
      title: `Restore ${order}?`,
      text:
        "This restores the exact backed-up booking to its original normal Booking Calendar location. An existing booking will never be overwritten.",
      icon: "warning",
      buttons: ["Cancel", "Restore booking"],
    });
    if (!confirmed) return;

    setWorkingId(record.id);
    try {
      await restoreDeletedBooking({ recoveryId: record.id });
      notifyTopRight("Booking restored to the normal calendar");
      await loadRecoveryRecords();
    } catch (error) {
      console.error("Unable to restore booking", error);
      notifyError(
        `Restore failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  const permanentlyDeleteBooking = async (record) => {
    if (
      !hasRecoveryDeleteAccess ||
      record.status !== "deleted" ||
      workingId
    ) {
      return;
    }

    const order = getSummaryValue(record, "order");
    const confirmed = await swal({
      title: `Permanently delete backup for ${order}?`,
      text:
        "This destroys the recovery copy and its audit log forever. It cannot be restored. Only an account approved in Recovery Delete Access can complete this action.",
      icon: "warning",
      buttons: ["Cancel", "Delete forever"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setWorkingId(record.id);
    try {
      await permanentlyDeleteDeletedBooking({ recoveryId: record.id });
      notifyTopRight("Booking recovery backup permanently deleted");
      await loadRecoveryRecords();
    } catch (error) {
      console.error("Unable to permanently delete booking recovery record", error);
      notifyError(
        `Permanent delete failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  const permanentlyDeleteEmptyHnRecords = async () => {
    if (!isSuperAdmin || !hasRecoveryDeleteAccess || workingId) return;

    const confirmed = await swal({
      title: "Permanently delete all empty-HN booking records?",
      text:
        `This permanently deletes all ${
          Number.isFinite(emptyHnCount) ? emptyHnCount : "currently matching"
        } Booking Recovery records with no HN, including recovery copies and audit logs older than the 250 records shown here. This cannot be undone. Records with an HN are not affected.`,
      icon: "warning",
      buttons: ["Cancel", "Delete all empty-HN records"],
      dangerMode: true,
    });
    if (!confirmed) return;

    setWorkingId(BULK_EMPTY_HN_WORKING_ID);
    try {
      const response = await permanentlyDeleteEmptyHnBookingRecoveryRecords();
      const permanentlyDeletedCount = Number(
        response &&
          response.data &&
          response.data.permanentlyDeletedCount,
      );

      setEmptyHnCount(0);

      notifyTopRight(
        permanentlyDeletedCount
          ? `${permanentlyDeletedCount} empty-HN booking recovery record${
              permanentlyDeletedCount === 1 ? "" : "s"
            } permanently deleted`
          : "No empty-HN booking recovery records were found",
      );
      await loadRecoveryRecords();
    } catch (error) {
      console.error(
        "Unable to permanently delete empty-HN booking recovery records",
        error,
      );
      notifyError(
        `Bulk permanent delete failed: ${(error && error.message) || error}`,
      );
    } finally {
      setWorkingId("");
    }
  };

  return (
    <>
      <Toast />
      <PageTitle
        activeMenu="Recovery & Delete Log"
        motherMenu="Booking"
        motherLink="/booking-calendar"
      />

      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center">
          <div>
            <h4 className="card-title mb-1">Recovery & Delete Log</h4>
            <small className="text-muted">
              Deleted bookings stay recoverable here with the operator and time
              recorded. Restoring never overwrites an active booking.
            </small>
            {isSuperAdmin ? (
              <div className="mt-2">
                <Badge
                  variant={
                    emptyHnCountLoading || !Number.isFinite(emptyHnCount)
                      ? "secondary"
                      : emptyHnCount > 0
                        ? "danger"
                        : "success"
                  }
                  className="px-2 py-1"
                >
                  Empty HN recovery cases:{" "}
                  {emptyHnCountLoading
                    ? "Loading…"
                    : Number.isFinite(emptyHnCount)
                      ? emptyHnCount
                      : "Unavailable"}
                </Badge>
              </div>
            ) : null}
          </div>
          <div className="d-flex mt-2 mt-md-0">
            {isSuperAdmin && hasRecoveryDeleteAccess ? (
              <Button
                variant="danger"
                size="sm"
                className="mr-2"
                onClick={permanentlyDeleteEmptyHnRecords}
                disabled={
                  loading ||
                  emptyHnCountLoading ||
                  !emptyHnCount ||
                  Boolean(workingId)
                }
              >
                {workingId === BULK_EMPTY_HN_WORKING_ID
                  ? "Deleting empty-HN cases…"
                  : "Delete all empty-HN cases"}
              </Button>
            ) : null}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadRecoveryRecords}
              disabled={
                loading || emptyHnCountLoading || Boolean(workingId)
              }
            >
              Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={5} lg={5}>
              <Form.Control
                type="search"
                placeholder="Search order, HN, patient, node, or deleting user"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </Col>
            <Col md={4} lg={3} className="mt-2 mt-md-0">
              <Form.Control
                as="select"
                value={hnFilter}
                onChange={(event) => setHnFilter(event.target.value)}
                aria-label="Filter booking recovery records by HN"
              >
                <option value={RECOVERY_HN_FILTER_WITH_HN}>
                  Hide empty HN cases
                </option>
                <option value={RECOVERY_HN_FILTER_EMPTY}>
                  Show empty HN cases only
                </option>
                <option value={RECOVERY_HN_FILTER_ALL}>Show all cases</option>
              </Form.Control>
            </Col>
            <Col className="text-md-right mt-2 mt-md-0 text-muted">
              {visibleRecords.length} record{visibleRecords.length === 1 ? "" : "s"}
            </Col>
          </Row>

          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>Deleted at</th>
                  <th>Order / HN</th>
                  <th>Patient / Node</th>
                  <th>Booking status</th>
                  <th>Deleted by</th>
                  <th>Recovery status</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      Loading recovery records…
                    </td>
                  </tr>
                ) : visibleRecords.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No deleted booking records found.
                    </td>
                  </tr>
                ) : (
                  visibleRecords.map((record) => {
                    const deletedBy = record.deletedBy || {};
                    const isDeleted = record.status === "deleted";
                    const isWorking = workingId === record.id;

                    return (
                      <tr key={record.id}>
                        <td>{formatDateTime(record.deletedAt)}</td>
                        <td>
                          <strong>{getSummaryValue(record, "order")}</strong>
                          <br />
                          <small className="text-muted">
                            HN: {getSummaryValue(record, "hn")}
                          </small>
                        </td>
                        <td>
                          {getSummaryValue(record, "patientName")}
                          <br />
                          <small className="text-muted">
                            {getSummaryValue(record, "node")}
                          </small>
                        </td>
                        <td>{getSummaryValue(record, "status")}</td>
                        <td>
                          {deletedBy.displayName || "Unknown"}
                          {deletedBy.email ? (
                            <>
                              <br />
                              <small className="text-muted">{deletedBy.email}</small>
                            </>
                          ) : null}
                        </td>
                        <td>
                          <Badge variant={isDeleted ? "warning" : "success"}>
                            {isDeleted ? "Deleted / recoverable" : "Restored"}
                          </Badge>
                          {record.deleteMethod === "safety_net_untracked_delete" ? (
                            <>
                              <br />
                              <small className="text-danger">
                                Captured outside the app; actor unavailable
                              </small>
                            </>
                          ) : null}
                        </td>
                        <td className="text-right text-nowrap">
                          {isDeleted ? (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => restoreBooking(record)}
                              disabled={Boolean(workingId)}
                            >
                              {isWorking ? "Working…" : "Restore"}
                            </Button>
                          ) : (
                            <span className="text-muted">Recovered</span>
                          )}
                          {isDeleted && hasRecoveryDeleteAccess ? (
                            <Button
                              size="sm"
                              variant="danger"
                              className="ml-2"
                              onClick={() => permanentlyDeleteBooking(record)}
                              disabled={Boolean(workingId)}
                            >
                              Delete forever
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};

export default BookingRecovery;
